import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getKitchenQueue, getBranches, updateOrderKitchenStatus } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import PageHeader from "../components/UI/PageHeader";
import { notify } from "../utils/notify";
import { useTranslation } from "../context/I18nContext";

// Mirrors models.OrderKitchenStatus* in order.go exactly — the order this
// array lists them in IS the forward progression a dine-in order's kitchen
// status moves through, one step at a time (see handleAdvance below). Note
// "served" never actually shows up as a column here — once an order reaches
// it, ListKitchenOrdersHandler stops returning it at all (see its own doc
// comment in order_handler.go), so the board naturally drops a ticket the
// moment it's marked served.
const KITCHEN_STATUS_FLOW = ["pending", "preparing", "ready", "served"];

const REFRESH_INTERVAL_MS = 20000;

function minutesAgo(dateStr) {
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000));
}

export default function KitchenDisplay() {
  const { t } = useTranslation("kitchen_display");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [orders, setOrders] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const branchOptions = [{ value: "", label: t("filter_branch_all_option", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))];

  useEffect(() => { getBranches(token).then(setBranches).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // `silent` skips the loading spinner and error toast for the background
  // auto-refresh below — a transient blip every 20s shouldn't flash the
  // whole board or nag the kitchen with a toast; the manual "Refresh"
  // button (silent=false) is what surfaces a real failure.
  const fetchQueue = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (branchFilter) params.branch_id = branchFilter;
      const data = await getKitchenQueue(token, params);
      setOrders(data || []);
      setLastRefreshedAt(new Date());
    } catch (err) {
      if (!silent) notify.error(err.data?.error || err.message || t("notify_load_failed", "Failed to load the kitchen queue"));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchQueue(false); }, [branchFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh — a Kitchen Display board is meant to be left open on a
  // screen in the kitchen, unattended, so it has to keep itself current on
  // its own rather than waiting for someone to click Refresh.
  useEffect(() => {
    const interval = setInterval(() => fetchQueue(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [branchFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedByStatus = useMemo(() => {
    const groups = { pending: [], preparing: [], ready: [] };
    orders.forEach((o) => { if (groups[o.kitchen_status]) groups[o.kitchen_status].push(o); });
    return groups;
  }, [orders]);

  // Moves one ticket forward through KITCHEN_STATUS_FLOW — see
  // UpdateKitchenStatusHandler in order_handler.go, which is the actual
  // authority on whether this is allowed. A silent refetch right after
  // picks up the server's own state (and naturally drops the card once it's
  // "served" — see ListKitchenOrdersHandler's own doc comment).
  const handleAdvance = async (order) => {
    const next = KITCHEN_STATUS_FLOW[KITCHEN_STATUS_FLOW.indexOf(order.kitchen_status) + 1];
    if (!next) return;
    setUpdatingId(order.id);
    try {
      await updateOrderKitchenStatus(token, order.id, next);
      fetchQueue(true);
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_update_failed", "Failed to update this order"));
    } finally {
      setUpdatingId(null);
    }
  };

  const COLUMNS = [
    { key: "pending", label: t("column_pending", "Pending"), badgeClass: "bg-gray-100 text-gray-500", advanceLabel: t("btn_start_preparing", "Start Preparing") },
    { key: "preparing", label: t("column_preparing", "Preparing"), badgeClass: "bg-amber-50 text-amber-600", advanceLabel: t("btn_mark_ready", "Mark Ready") },
    { key: "ready", label: t("column_ready", "Ready"), badgeClass: "bg-blue-50 text-blue-600", advanceLabel: t("btn_mark_served", "Mark Served") },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.KitchenDisplay size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Kitchen Display")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Every dine-in order still being worked on")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <div className="w-full sm:w-56">
            <SelectBox options={branchOptions} value={branchFilter} onChange={setBranchFilter} label={t("filter_branch_label", "Filter by branch")} />
          </div>
          <Button variant="outline" icon={ICONS.Refresh} onClick={() => fetchQueue(false)} className="w-full sm:w-auto shrink-0">
            {t("btn_refresh", "Refresh")}
          </Button>
        </div>
      </PageHeader>

      {lastRefreshedAt && (
        <p className="text-[11px] text-gray-400 -mt-4 mb-4">
          {t("label_last_refreshed", "Last updated {time}").replace("{time}", lastRefreshedAt.toLocaleTimeString())}
        </p>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-400">{t("loading_label", "Loading…")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {COLUMNS.map((col) => {
            const colOrders = groupedByStatus[col.key] || [];
            return (
              <div key={col.key} className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-4 flex flex-col min-h-[300px]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-black text-gray-800">{col.label}</h3>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${col.badgeClass}`}>{colOrders.length}</span>
                </div>

                {colOrders.length === 0 && (
                  <p className="text-gray-300 text-sm italic text-center py-8">{t("empty_column_hint", "Nothing here")}</p>
                )}

                <div className="space-y-3 overflow-y-auto">
                  {colOrders.map((order) => {
                    const waitMins = minutesAgo(order.created_at);
                    const waitClass = waitMins >= 30 ? "text-red-500" : waitMins >= 15 ? "text-amber-600" : "text-gray-400";
                    return (
                      <div key={order.id} className="border border-gray-100 rounded-2xl p-3">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-bold text-sm text-gray-800">
                              {order.table ? `${order.table.name}${order.table.section ? ` (${order.table.section.name})` : ""}` : t("label_no_table", "No table")}
                            </div>
                            <div className="text-[10px] text-gray-400 font-mono">{t("label_order_number", "Order #{id}").replace("{id}", order.id)}</div>
                          </div>
                          <span className={`text-[10px] font-bold uppercase shrink-0 ${waitClass}`}>
                            {t("label_minutes_ago", "{mins}m").replace("{mins}", waitMins)}
                          </span>
                        </div>

                        <div className="space-y-0.5 mb-3">
                          {(order.items || []).map((item) => (
                            <div key={item.id} className="text-xs text-gray-600 flex justify-between gap-2">
                              <span className="truncate">{item.product_name}</span>
                              <span className="font-mono text-gray-400 shrink-0">× {item.display_quantity || item.quantity}</span>
                            </div>
                          ))}
                          {(order.service_items || []).map((si) => (
                            <div key={`svc-${si.id}`} className="text-xs text-gray-600 flex justify-between gap-2">
                              <span className="truncate">{si.service_name}</span>
                              <span className="font-mono text-gray-400 shrink-0">×1</span>
                            </div>
                          ))}
                        </div>

                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full"
                          onClick={() => handleAdvance(order)}
                          loading={updatingId === order.id}
                        >
                          {col.advanceLabel}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
